import { z } from "zod";

export const TierConfigSchema = z.record(z.string(), z.unknown());
export type TierConfig = z.infer<typeof TierConfigSchema>;

// Flat threshold shape used by the public tier-config API.
// The DB stores a nested form (high.extended_rate_gte, etc.) consumed by methodology;
// the backend translates between flat and nested at the boundary.
export const TIER_THRESHOLD_DEFAULTS = {
  high_extended_rate: 40,
  high_fleet_idle_rate: 75,
  watch_list_extended_rate: 25,
  watch_list_fleet_idle_rate: 50,
  on_track_extended_rate: 10,
  on_track_fleet_idle_rate: 25,
} as const;

const thresholdField = z.number().min(0).max(100);

export const TierThresholdsSchema = z.object({
  high_extended_rate: thresholdField,
  high_fleet_idle_rate: thresholdField,
  watch_list_extended_rate: thresholdField,
  watch_list_fleet_idle_rate: thresholdField,
  on_track_extended_rate: thresholdField,
  on_track_fleet_idle_rate: thresholdField,
});
export type TierThresholds = z.infer<typeof TierThresholdsSchema>;

// The geographic market a client belongs to. Industry insights (benchmarks,
// peer comparison) are scoped per region so UK clients never get averaged
// together with AU ones — see insights.ts.
export const REGIONS = ["AU", "UK", "NZ"] as const;
export type Region = (typeof REGIONS)[number];

export const ClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, "slug must be lowercase letters, digits, and hyphens only"),
  region: z.enum(REGIONS),
  industries: z.array(z.string()),
  tierConfig: TierConfigSchema.nullable(),
  pathfinderUsername: z.string().nullable(),
  googleDriveUrl: z.string().url().nullable(),
  canvaUrl: z.string().url().nullable(),
  contractEndDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  falconCount: z.number().int().nonnegative().nullable(),
  eagleCount: z.number().int().nonnegative().nullable(),
  appOnly: z.boolean().nullable(),
  tablets: z.boolean().nullable(),
  simProvider: z.enum(["Telstra", "Optus"]).nullable(),
  customEscalation: z.boolean().nullable(),
  receivesMonthlyReports: z.boolean().nullable(),
  arr: z.number().int().nonnegative().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});

export type Client = z.infer<typeof ClientSchema>;

// List entries include a precomputed reporting-period count, the composite
// Customer Health Score for the most recent period (null if no periods yet),
// the prior period's score (null if there's fewer than two periods) so
// the Overview "Recent changes" panel can render a delta without re-fetching,
// and the YYYY-MM of the most recent period (null if no periods) so the UI
// can flag stale data when no upload has happened in a while.
// CSM fields are joined from the users table for the assigned CSM (if any).
export const ClientListItemSchema = ClientSchema.extend({
  periodCount: z.number().int().nonnegative(),
  healthScore: z.number().int().min(0).max(100).nullable(),
  previousHealthScore: z.number().int().min(0).max(100).nullable(),
  latestPeriodAt: z.string().regex(/^\d{4}-\d{2}$/).nullable(),
  csmUserId: z.string().uuid().nullable(),
  csmName: z.string().nullable(),
  csmEmail: z.string().nullable(),
  // Whether an App User roster has ever been uploaded for this client --
  // derived from the app_user_records table, not a flag anyone sets. This is
  // what decides whether the App Usage tab appears for a non-App-Only client
  // (mixed fleet): uploading a roster is what makes it show up, not a
  // checkbox someone has to remember to flip. appOnly (above) only decides
  // whether App Usage *replaces* the device tabs or sits alongside them.
  hasAppUsers: z.boolean(),
  // Only computed for App-Only clients (null otherwise) -- the share of
  // registered app users who show up in any check-in/emergency/team-alert
  // activity. Surfaced on the Clients page in place of Health Score, which
  // an App-Only client can never have (no device/session data to compute it
  // from) -- see AppUsage.tsx's identically-named tile for the full
  // definition and ClientsPage.tsx's HealthBadge for where this renders.
  appAdoptionRate: z.number().nullable(),
});
export type ClientListItem = z.infer<typeof ClientListItemSchema>;

export const ClientListResponseSchema = z.array(ClientListItemSchema);

export const UpdateClientCsmRequestSchema = z.object({
  userId: z.string().uuid().nullable(),
});
export type UpdateClientCsmRequest = z.infer<typeof UpdateClientCsmRequestSchema>;

export const CreateClientRequestSchema = z.object({
  name: z.string().min(1),
  industries: z.array(z.string()).optional().default([]),
  region: z.enum(REGIONS).optional().default("AU"),
});
export type CreateClientRequest = z.infer<typeof CreateClientRequestSchema>;

export const UpdateClientRequestSchema = z.object({
  industries: z.array(z.string()),
});
export type UpdateClientRequest = z.infer<typeof UpdateClientRequestSchema>;

export const UpdateClientInfoRequestSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  region: z.enum(REGIONS),
  pathfinderUsername: z.string().max(200).nullable(),
  googleDriveUrl: z.string().url().nullable().or(z.literal("").transform(() => null)),
  canvaUrl: z.string().url().nullable().or(z.literal("").transform(() => null)),
  contractEndDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  falconCount: z.number().int().nonnegative().nullable(),
  eagleCount: z.number().int().nonnegative().nullable(),
  appOnly: z.boolean().nullable(),
  tablets: z.boolean().nullable(),
  simProvider: z.enum(["Telstra", "Optus"]).nullable(),
  customEscalation: z.boolean().nullable(),
  receivesMonthlyReports: z.boolean().nullable(),
  arr: z.number().int().nonnegative().nullable(),
});
export type UpdateClientInfoRequest = z.infer<typeof UpdateClientInfoRequestSchema>;

export const ClientContactSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  name: z.string().min(1),
  role: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type ClientContact = z.infer<typeof ClientContactSchema>;

export const ClientContactListResponseSchema = z.object({
  items: z.array(ClientContactSchema),
});
export type ClientContactListResponse = z.infer<typeof ClientContactListResponseSchema>;

const contactFields = {
  name: z.string().min(1).max(200),
  role: z.string().max(200).nullable(),
  phone: z.string().max(50).nullable(),
  email: z.string().email().max(200).nullable().or(z.literal("").transform(() => null)),
} as const;

export const CreateClientContactRequestSchema = z.object(contactFields);
export type CreateClientContactRequest = z.infer<typeof CreateClientContactRequestSchema>;

export const UpdateClientContactRequestSchema = z.object(contactFields);
export type UpdateClientContactRequest = z.infer<typeof UpdateClientContactRequestSchema>;

export const ClientNoteSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  title: z.string().min(1),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD"),
  body: z.string(),
  createdAt: z.string().datetime({ offset: true }),
});

export type ClientNote = z.infer<typeof ClientNoteSchema>;

const noteFields = {
  title: z.string().min(1).max(200),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD"),
  body: z.string().max(10_000),
} as const;

export const CreateClientNoteRequestSchema = z.object(noteFields);
export type CreateClientNoteRequest = z.infer<typeof CreateClientNoteRequestSchema>;

export const UpdateClientNoteRequestSchema = z.object(noteFields);
export type UpdateClientNoteRequest = z.infer<typeof UpdateClientNoteRequestSchema>;

export const ClientNoteListResponseSchema = z.object({
  items: z.array(ClientNoteSchema),
});
export type ClientNoteListResponse = z.infer<typeof ClientNoteListResponseSchema>;

// Aggregated KPI snapshot for cross-client comparison. KPIs are averaged
// across all of a client's periods (or scoped to a single month). The
// `active_stores`/`total_stores` fields from the methodology output are
// surfaced as `active_teams`/`total_teams` here — language convention.
export const ClientSnapshotKpisSchema = z.object({
  total_sessions: z.number(),
  productive_hours: z.number(),
  total_hours: z.number(),
  extended_session_rate: z.number(),
  fleet_idle_rate_pct: z.number(),
  network_adoption_pct: z.number(),
  active_teams: z.number(),
  total_teams: z.number(),
  micro_sessions_excluded: z.number(),
});
export type ClientSnapshotKpis = z.infer<typeof ClientSnapshotKpisSchema>;

export const ClientSnapshotSchema = z.object({
  clientId: z.string().uuid(),
  clientName: z.string(),
  slug: z.string(),
  industries: z.array(z.string()),
  periodCount: z.number().int().nonnegative(),
  availableMonths: z.array(z.string()),
  kpis: ClientSnapshotKpisSchema,
});
export type ClientSnapshot = z.infer<typeof ClientSnapshotSchema>;
